// @ts-check

/**
 * Lazy-load a cross-sell section of collections.
 *
 * - This feature is only supported on collection and product pages.
 * - Collections for cross-selling are selected from the Main Menu menubar.
 *   - Jewelry collections are grouped by categories, e.g. "Rings", "Bracelets",
 *     etc.
 *   - Watches and Objects collections have their respective categories grouped
 *     together.
 *   - In all cases:
 *     - Category titles starting with "All " and category titles of "SPECIAL"
 *       are ignored.
 *     - Subcategory titles of "-" (spacers) or missing collection images are
 *       also ignored.
 * - The cached Main Menu data expires one hour after request. Any changes to
 *   the link list or collection data such as images will become visible after
 *   its expiration time (`ttl`).
 *   - A cache bust is possible by incrementing the `SESSION_STORAGE_NAME`.
 */
(async function() {
  const SESSION_STORAGE_NAME = 'p4_menu_v3'; // To cache-bust, increment version.
  const MAIN_MENU_HANDLE = 'main-menu-copy';
  const CATEGORY_COUNT = 6;
  const CONTAINER = document.querySelector('#js-collection-rec');
  const CONTAINER_HEADING = CONTAINER.querySelector('#js-collection-rec--heading');
  const CONTAINER_LIST = CONTAINER.querySelector('#js-collection-rec--list');

  const pageData = getPageData();
  if (!pageData) return;
  const crossSellData = await getCrossSellData(findActiveMenubarItems(), pageData.collectionId);
  if (!crossSellData || !crossSellData.collections) return;
  CONTAINER_HEADING.innerText = `Explore ${crossSellData.title}`;
  CONTAINER_LIST.innerHTML = makeHtml(crossSellData.collections, getPageSeed(pageData));
  CONTAINER.hidden = false;
  track();

  /**
   * @typedef {object} PageData
   * @property {SupportedPageTemplate} template
   * @property {string} [collectionId]
   * @property {string} [collectionPageNum]
   * @property {string} [productId]
   *
   * @typedef {'product' | 'collection'} SupportedPageTemplate
   *
   * @typedef {object} ActiveMenubarItems
   * @property {string | undefined} type
   * @property {string | undefined} category
   *
   * @typedef {object} CrossSellData
   * @property {string | undefined} title
   * @property {Collection[] | undefined} collections
   *
   * @typedef {object} Collection
   * @property {string} id
   * @property {string} title
   * @property {string} href
   * @property {string} imageSrc
   */

  /**
   * @returns {PageData | undefined}
   */
  function getPageData() {
    const template = document.body.dataset.template;
    switch(template) {
      case 'product': {
        return {
          template,
          productId: window.APP.productID,
        };
      }
      case 'collection': {
        const { collectionId, collectionPageNum } = document.getElementById('js-collection-main').dataset;
        return {
          template,
          collectionId,
          collectionPageNum,
        };
      }
    }
  }

  /**
    * Find active menubar type and category.
    *
    * The cross-sell category should be determined via the menubar. This should
    * be the case regardless of the page origin, whether the collection page or
    * the product page.
    *
    * @returns {ActiveMenubarItems}
    */
  function findActiveMenubarItems() {
    const activeType = document.querySelector('.menubar--menu[data-active-child="true"]');
    const activeTypeTitle = activeType?.querySelector('.menubar--link__top')?.textContent;
    const activeCategory = activeType?.querySelector('.menubar--category[data-active-child="true"]');
    const activeCategoryTitle = activeCategory?.querySelector('.menubar--category-title')?.textContent;

    return {
      type: activeTypeTitle,
      category: activeCategoryTitle,
    };
  }

  /**
   * @param {ActiveMenubarItems} activeMenubarItems
   * @param {string} [excludeCollectionId]
   * @returns {Promise<CrossSellData | undefined>}
   */
  async function getCrossSellData(activeMenubarItems, excludeCollectionId) {
    if (!activeMenubarItems.type || !activeMenubarItems.category) return;

    const mainMenu = await fetchMainMenu();
    const crossSellData = (activeMenubarItems.type === 'Jewelry')
      ? {
        title: activeMenubarItems.category,
        collections: mainMenu[activeMenubarItems.type][activeMenubarItems.category],
      }
      : {
        title: activeMenubarItems.type,
        collections: mainMenu[activeMenubarItems.type],
      };

    if (excludeCollectionId) {
      crossSellData.collections = crossSellData.collections.filter(c => c.id !== excludeCollectionId);
    }

    return crossSellData;
  }

  /**
   * Get the main menu data via session storage, if available, or the Storefront
   * API, caching the resultant data.
   *
   * Select categories and collections are removed under `isIncludedCategory()`
   * and `formatSubcategories()`.
   *
   * @returns {Promise<object>}
   */
  async function fetchMainMenu() {
    const json = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_NAME));
    if (json && json.ttl > Date.now()) return json.data;

    const payload = await window.APP.queryStorefront({
      query: /* graphql */`
        query MainMenu3 {
          mainMenu: menu(handle: "${MAIN_MENU_HANDLE}") {
            types: items {
              title
              categories: items {
                title
                subcategories: items {
                  collection: resource {
                    ... on Collection {
                      id
                      title
                      onlineStoreUrl
                      image {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `
    });
    if (!payload) throw new Error(`Link list not found: ${MAIN_MENU_HANDLE}`);

    const mainMenu = (function formatMainMenu(payload) {
      const formattedMainMenu = {};
      payload.mainMenu.types.forEach(type => {
        switch(type.title) {
          case 'Jewelry': {
            const categories = {};
            type.categories.forEach(category => {
              if (!isIncludedCategory(category.title)) return;
              categories[category.title] = formatSubcategories(category.subcategories);
            });
            formattedMainMenu[type.title] = categories;
            break;
          }

          case 'Watches':
          case 'Objects': {
            const combinedSubcategories = [];
            type.categories.forEach(category => {
              combinedSubcategories.push(...formatSubcategories(category.subcategories));
            });
            formattedMainMenu[type.title] = combinedSubcategories;
            break;
          }
        }
      });
      return formattedMainMenu;

      /**
       * Prevent select categories from being included.
       * @param {string} title
       * @returns {boolean}
       */
      function isIncludedCategory(title) {
        const normalizedTitle = title.trim().toLowerCase();
        if (normalizedTitle === 'special') return false;
        if (normalizedTitle.startsWith('all ')) return false;
        return true;
      }

      /**
       * Format collection items while removing:
       *   - Spacers, i.e. menu items titled "-".
       *   - Collections without images.
       * @param {object[]} subcategories
       * @returns {Collection[]}
       */
      function formatSubcategories(subcategories) {
        return subcategories
          .filter(({ collection }) => collection !== null)
          .filter(({ collection }) => collection.image !== null)
          .map(({ collection }) => ({
            id: collection.id.replace('gid://shopify/Collection/', ''),
            title: collection.title,
            href: collection.onlineStoreUrl,
            imageSrc: collection.image.url,
          }));
      }
    })(payload);

    const oneHourFromNow = Date.now() + 60 * 60 * 1000;
    const storage = JSON.stringify({
      ttl: oneHourFromNow,
      data: mainMenu,
    });
    sessionStorage.setItem(SESSION_STORAGE_NAME, storage);

    return mainMenu;
  }

  /**
   * @param {PageData} pageData
   * @returns {number}
   */
  function getPageSeed(pageData) {
    if (pageData.template === 'product')
      return Number(pageData.productId);
    if (pageData.template === 'collection')
      return Number(pageData.collectionId) + Number(pageData.collectionPageNum);
  }

  /**
   * @param {Collection[]} collections
   * @param {number} seed
   * @returns
   */
  function makeHtml(collections, seed) {
    const template = window.tmpl('jsCollectionTemplate');
    return pseudoRandomSequence(seed, collections.length - 1, CATEGORY_COUNT).reduce((html, index) => {
      return html + template({
        id: collections[index].id,
        title: collections[index].title,
        link: collections[index].href,
        img: collections[index].imageSrc,
      });
    }, '');
  }

  /**
   * Generate a pseudo-randomly shuffled array of given length whose values are
   * integers 0..(length - 1) in single increments, e.g. `[2, 0, 1]`. Identical
   * parameters should always return identical results.
   *
   * @param {number} seed
   * @param {number} maxNumber
   * @param {number} returnCount
   * @returns {number[]}
   */
  function pseudoRandomSequence(seed, maxNumber, returnCount) {
    const length = maxNumber + 1;
    const veryLargeNumber = 1234567890; // See `sfc32()`.
    const seededRandom = sfc32(...makeSeeds(seed).map(n => n * veryLargeNumber));
    const sequence = [...Array(length).keys()]; // Ex. [0, 1, 2, 3]
    return shuffle(sequence, seededRandom).slice(0, returnCount);

    /**
     * @param {number[]} array
     * @param {function(): number} randomFunction
     * @see https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
     */
    function shuffle(array, randomFunction) {
      const copy = [...array];
      let currentIndex = copy.length;
      while (currentIndex != 0) {
        let randomIndex = Math.floor(randomFunction() * currentIndex);
        currentIndex--;
        [copy[currentIndex], copy[randomIndex]] = [copy[randomIndex], copy[currentIndex]];
      }
      return copy;
    }

    /**
     * Split a given seed number into four separate seed numbers.
     *
     * ```
     * 1234 → [1, 2, 3, 4]
     * 12345 → [1, 2, 3, 45]
     * 123456 → [1, 2, 3, 456]
     * 1234567 → [12, 34, 56, 7]
     * ```
     *
     * @param {number} num
     * @returns {[number, number, number, number]}
     */
    function makeSeeds(num) {
      const COUNT = 4;
      const str = num.toString();
      const strLength = str.length;
      if (strLength < COUNT) throw new Error('Number must be at least four digits.');
      const chunkLength = -Math.round(strLength / -COUNT); // Round DOWN until `1.5`.
      let chunks = [];
      for (let i = 0; i < COUNT; i++) {
        const start = i * chunkLength;
        const end = start + chunkLength;
        chunks.push(Number(
          i === (COUNT - 1) ?
            str.slice(start, strLength) :
            str.slice(start ,end)
        ));
      }
      return chunks;
    }

    /**
     * Very fast, very non-cryptographic, seeded pseudorandom number generator.
     * Seeds must be sufficiently large, aim for at least 12 digits.
     *
     * @param {number} a
     * @param {number} b
     * @param {number} c
     * @param {number} d
     * @returns {function(): number}
     * @see https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript
     */
    function sfc32(a, b, c, d) {
      return function() {
        a |= 0; b |= 0; c |= 0; d |= 0;
        let t = (a + b | 0) + d | 0;
        d = d + 1 | 0;
        a = b ^ b >>> 9;
        b = c + (c << 3) | 0;
        c = (c << 21 | c >>> 11);
        c = c + t | 0;
        return (t >>> 0) / 4294967296;
      }
    }
  }

  function track() {
    if (typeof gtag === 'undefined') return;

    const baseEventParams = {
      page_type: document.body.dataset.template,
      parent_category: crossSellData.title,
    };

    // Track visibility.
    window.APP.trackVisibilityOnce(CONTAINER, 'cross_sell_collection_view', baseEventParams);

    // Track clicks.
    CONTAINER_LIST.querySelectorAll('a').forEach(link => {
      window.APP.trackClick(link, 'cross_sell_collection_click', {
        ...baseEventParams,
        link_id: link.dataset.id,
        link_title: link.dataset.title,
      });
    });
  }
})();
